import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import showToast from '@/components/ui/Toast';
import { cashflowService } from '@/services/cashflowService';
import type {
  CashflowAccount,
  CashflowTransaction,
  CreateAccountPayload,
  CreateManualTransactionPayload,
  UpdateAccountPayload,
  UpdateManualTransactionPayload,
} from '@/types/cashflow';
import { CashflowSummaryCards } from '@/components/community/cashflow/CashflowSummaryCards';
import { CashflowTransactionList } from '@/components/community/cashflow/CashflowTransactionList';
import { CashflowPositionCard } from '@/components/community/cashflow/CashflowPositionCard';
import { CashflowMonthlyFlowCard } from '@/components/community/cashflow/CashflowMonthlyFlowCard';
import { CashflowYearlyTable } from '@/components/community/cashflow/CashflowYearlyTable';
import { CashflowAddActionBottomSheet } from '@/components/community/cashflow/CashflowAddActionBottomSheet';
import { CashflowAddAccountModal } from '@/components/community/cashflow/CashflowAddAccountModal';
import { CashflowManualTransactionModal } from '@/components/community/cashflow/CashflowManualTransactionModal';
import { CashflowOnboarding } from '@/components/community/cashflow/CashflowOnboarding';
import { getKYCStatus, getWalletData, getBankAccount } from '@/services/walletService';
import communityService from '@/services/communityService';

export const CashflowManagementPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState<number>(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(now.getFullYear());
  const [txFilter, setTxFilter] = useState<string>('all');
  const [page, setPage] = useState<number>(1);

  // Sync month from query param if available
  useEffect(() => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      const m = parseInt(monthParam, 10);
      if (m >= 1 && m <= 12) setSelectedMonth(m);
    }
  }, [searchParams]);

  // Modals states
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const [isManualTxModalOpen, setIsManualTxModalOpen] = useState(false);
  const [txToEdit, setTxToEdit] = useState<CashflowTransaction | null>(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [accountToEdit, setAccountToEdit] = useState<CashflowAccount | null>(null);
  const [isYearlyModalOpen, setIsYearlyModalOpen] = useState(false);

  // When tapping a specific month in Arus Kas Bulanan table
  const handleSelectMonth = (monthNum: number) => {
    setSelectedMonth(monthNum);
    setPage(1);
    const el = document.getElementById('catatan-kas-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToAccounts = () => {
    const el = document.getElementById('posisi-dana-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Listen for export button clicked on MobileTopbar
  useEffect(() => {
    const handleExportTrigger = () => {
      setIsYearlyModalOpen(true);
    };
    window.addEventListener('sporto-cashflow-export', handleExportTrigger);
    return () => {
      window.removeEventListener('sporto-cashflow-export', handleExportTrigger);
    };
  }, []);

  // Month navigation helpers
  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear((prev) => prev - 1);
    } else {
      setSelectedMonth((prev) => prev - 1);
    }
    setPage(1);
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear((prev) => prev + 1);
    } else {
      setSelectedMonth((prev) => prev + 1);
    }
    setPage(1);
  };

  const { data: profileRes } = useQuery({
    queryKey: ['communityProfile'],
    queryFn: () => communityService.getProfile(),
    staleTime: 1000 * 60 * 5,
  });
  const communityName = profileRes?.data?.community?.name || '';

  const { data: accountsData, isLoading: isLoadingAccounts } = useQuery({
    queryKey: ['cashflow', 'accounts'],
    queryFn: () => cashflowService.getAccounts(),
  });

  const { data: kycRes, isLoading: isLoadingKYC } = useQuery({
    queryKey: ['wallet', 'kyc'],
    queryFn: () => getKYCStatus(),
  });

  const { data: walletRes } = useQuery({
    queryKey: ['wallet', 'data'],
    queryFn: () => getWalletData(),
  });

  const { data: bankAccountRes } = useQuery({
    queryKey: ['wallet', 'bankAccount'],
    queryFn: () => getBankAccount(),
  });

  const { data: yearlyReport, isLoading: isLoadingYearly } = useQuery({
    queryKey: ['cashflow', 'yearly', selectedYear],
    queryFn: () => cashflowService.getYearlyReport(selectedYear),
  });

  const { data: summary, isLoading: isLoadingSummary } = useQuery({
    queryKey: ['cashflow', 'summary', selectedMonth, selectedYear],
    queryFn: () => cashflowService.getSummary(selectedMonth, selectedYear),
  });

  const { data: txData, isLoading: isLoadingTx } = useQuery({
    queryKey: ['cashflow', 'transactions', selectedMonth, selectedYear, txFilter, page],
    queryFn: () =>
      cashflowService.getTransactions({
        month: selectedMonth,
        year: selectedYear,
        filter: txFilter,
        page,
        limit: 10,
      }),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['cashflow', 'categories'],
    queryFn: () => cashflowService.getCategories(),
  });

  // Invalidate all cashflow queries helper
  const invalidateCashflow = () => {
    queryClient.invalidateQueries({ queryKey: ['cashflow'] });
  };

  // Account Mutations
  const createAccountMutation = useMutation({
    mutationFn: (payload: CreateAccountPayload) => cashflowService.createAccount(payload),
    onSuccess: () => {
      showToast.success('Kantong kas berhasil ditambahkan');
      invalidateCashflow();
    },
    onError: (err: Error) => {
      showToast.error(err.message || 'Gagal menambahkan kantong kas');
    },
  });

  const updateAccountMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateAccountPayload }) =>
      cashflowService.updateAccount(id, payload),
    onSuccess: () => {
      showToast.success('Kantong kas berhasil diperbarui');
      invalidateCashflow();
    },
    onError: (err: Error) => {
      showToast.error(err.message || 'Gagal memperbarui kantong kas');
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: (id: number) => cashflowService.deleteAccount(id),
    onSuccess: () => {
      showToast.success('Kantong kas berhasil dihapus');
      invalidateCashflow();
    },
    onError: (err: Error) => {
      showToast.error(err.message || 'Gagal menghapus kantong kas');
    },
  });

  // Manual Transaction Mutations
  const createTxMutation = useMutation({
    mutationFn: (payload: CreateManualTransactionPayload) =>
      cashflowService.createManualTransaction(payload),
    onSuccess: () => {
      showToast.success('Catatan transaksi berhasil disimpan');
      invalidateCashflow();
    },
    onError: (err: Error) => {
      showToast.error(err.message || 'Gagal mencatat transaksi');
    },
  });

  const updateTxMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: UpdateManualTransactionPayload }) =>
      cashflowService.updateManualTransaction(id, payload),
    onSuccess: () => {
      showToast.success('Catatan transaksi berhasil diperbarui');
      invalidateCashflow();
    },
    onError: (err: Error) => {
      showToast.error(err.message || 'Gagal memperbarui transaksi');
    },
  });

  const deleteTxMutation = useMutation({
    mutationFn: (id: number) => cashflowService.deleteManualTransaction(id),
    onSuccess: () => {
      showToast.success('Catatan transaksi berhasil dihapus');
      invalidateCashflow();
    },
    onError: (err: Error) => {
      showToast.error(err.message || 'Gagal menghapus transaksi');
    },
  });

  // Handlers for Add/Edit
  const handleSaveAccount = async (payload: CreateAccountPayload | UpdateAccountPayload) => {
    if (accountToEdit) {
      await updateAccountMutation.mutateAsync({ id: accountToEdit.id, payload });
    } else {
      await createAccountMutation.mutateAsync(payload as CreateAccountPayload);
    }
  };

  const handleDeleteAccount = (acc: CashflowAccount) => {
    if (window.confirm(`Yakin ingin menghapus kantong kas "${acc.name}"?`)) {
      deleteAccountMutation.mutate(acc.id);
    }
  };

  const handleSaveManualTx = async (
    payload: CreateManualTransactionPayload | UpdateManualTransactionPayload
  ) => {
    if (txToEdit) {
      await updateTxMutation.mutateAsync({ id: txToEdit.id, payload });
    } else {
      await createTxMutation.mutateAsync(payload as CreateManualTransactionPayload);
    }
  };

  const handleDeleteTx = (tx: CashflowTransaction) => {
    if (window.confirm(`Yakin ingin menghapus catatan transaksi "${tx.title}"?`)) {
      deleteTxMutation.mutate(tx.id);
    }
  };

  // Check if onboarding is needed: community has no manual accounts in cashflow
  const hasManualAccount = accountsData?.accounts?.some((a) => !a.is_sporto_wallet);

  if (isLoadingAccounts || isLoadingKYC) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#FC480D] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium text-gray-500">Memuat data Cash Flow...</span>
        </div>
      </div>
    );
  }

  if (!hasManualAccount) {
    return (
      <CashflowOnboarding
        kycStatus={kycRes?.kyc_status}
        kycRequest={kycRes?.kyc_request}
        bankAccount={bankAccountRes}
        walletBalance={walletRes?.balance ?? 0}
        onActivated={invalidateCashflow}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0E1726] pb-24 md:pb-12">
      <div className="max-w-xl md:max-w-7xl mx-auto px-4 sm:px-6 pt-3 sm:pt-4 space-y-4 sm:space-y-6">
        {/* Desktop Header Banner matching Screenshot 1 */}
        <div className="hidden md:flex items-center justify-between pb-1">
          <div>
            <h1 className="text-2xl font-extrabold text-[#0E1726] tracking-tight">
              Cash Flow
            </h1>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => setIsYearlyModalOpen(true)}
              className="px-4 py-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
            >
              <Icon icon="ph:export-bold" className="w-4 h-4 text-gray-500" />
              <span>Export laporan</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setTxToEdit(null);
                setIsManualTxModalOpen(true);
              }}
              className="px-4 py-2.5 rounded-xl bg-[#FC480D] hover:bg-[#e03d07] active:scale-[0.99] text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
            >
              <Icon icon="ph:plus-bold" className="w-4 h-4" />
              <span>Catat pengeluaran</span>
            </button>
          </div>
        </div>

        {/* 1. 4 Summary Cards (1 Row of 4 Cards on Desktop, 2x2 Grid on Mobile) */}
        <CashflowSummaryCards
          summary={summary ?? null}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          isLoading={isLoadingSummary}
          onOpenAccounts={handleScrollToAccounts}
        />

        {/* 2. Card 1: Catatan Kas (Full Width Table on Desktop, List on Mobile) */}
        <div id="catatan-kas-section">
          <CashflowTransactionList
            transactions={txData?.transactions ?? []}
            totalCount={txData?.total_count ?? 0}
            manualCount={txData?.manual_count ?? 0}
            currentFilter={txFilter}
            onFilterChange={(f) => {
              setTxFilter(f);
              setPage(1);
            }}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            isLoading={isLoadingTx}
            page={page}
            totalPages={txData?.total_pages ?? 1}
            onPageChange={setPage}
            onEditTransaction={(tx) => {
              setTxToEdit(tx);
              setIsManualTxModalOpen(true);
            }}
            onDeleteTransaction={handleDeleteTx}
            onAddTransaction={() => {
              setTxToEdit(null);
              setIsManualTxModalOpen(true);
            }}
          />
        </div>

        {/* 3. Card 2: Posisi Dana (Full Width Table on Desktop, List on Mobile) */}
        <div id="posisi-dana-section">
          <CashflowPositionCard
            accounts={accountsData?.accounts ?? []}
            totalCash={accountsData?.total_cash ?? 0}
            isLoading={isLoadingAccounts}
            onAddAccount={() => {
              setAccountToEdit(null);
              setIsAccountModalOpen(true);
            }}
            onEditAccount={(acc) => {
              if (acc.is_sporto_wallet || acc.is_kyc_wallet || (acc.notes && acc.notes.toLowerCase().includes('kyc'))) {
                return;
              }
              setAccountToEdit(acc);
              setIsAccountModalOpen(true);
            }}
            onDeleteAccount={handleDeleteAccount}
          />
        </div>

        {/* 4. Card 3: Arus Kas Bulanan (Full Width Table on Desktop, Compact on Mobile) */}
        <div id="arus-kas-section">
          <CashflowMonthlyFlowCard
            report={yearlyReport ?? null}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            currentMonth={selectedMonth}
            onSelectMonth={handleSelectMonth}
            isLoading={isLoadingYearly}
          />
        </div>

        {/* 5. Mobile-Only Bottom Export Laporan Button */}
        <button
          type="button"
          onClick={() => setIsYearlyModalOpen(true)}
          className="md:hidden w-full py-3.5 px-4 bg-white border border-[#E0E6ED] rounded-full text-sm font-bold text-[#0E1726] hover:bg-gray-50 active:scale-[0.99] transition-all flex items-center justify-center gap-2 shadow-2xs cursor-pointer"
        >
          <Icon icon="ph:export-bold" className="w-4 h-4 text-gray-700" />
          <span>Export laporan</span>
        </button>
      </div>

      {/* Floating Action Button (FAB) at Bottom Right - Mobile Only */}
      <button
        type="button"
        onClick={() => setIsActionSheetOpen(true)}
        className="md:hidden fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#FC480D] hover:bg-[#e03d07] active:scale-95 text-white flex items-center justify-center shadow-xl transition-all cursor-pointer"
        aria-label="Tambah catatan kas"
      >
        <Icon icon="ph:plus-bold" className="w-7 h-7" />
      </button>

      {/* Action Bottom Sheet (from FAB on Mobile) */}
      <CashflowAddActionBottomSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        onSelectManualTx={() => {
          setTxToEdit(null);
          setIsManualTxModalOpen(true);
        }}
        onSelectAddAccount={() => {
          setAccountToEdit(null);
          setIsAccountModalOpen(true);
        }}
        onSelectExportReport={() => {
          setIsYearlyModalOpen(true);
        }}
      />

      {/* Modal: Laporan Tahunan / Export */}
      {isYearlyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
            onClick={() => setIsYearlyModalOpen(false)}
          />
          <div className="relative w-full max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl z-10 space-y-4 max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-200">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto sm:hidden" />
            <div className="flex items-center justify-between border-b border-[#E0E6ED] pb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center">
                  <Icon icon="ph:chart-bar-bold" className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#0E1726]">Rekap Kas Tahunan</h3>
                  <p className="text-xs text-gray-400">Tahun {selectedYear}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsYearlyModalOpen(false)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors cursor-pointer"
              >
                <Icon icon="ph:x-bold" className="w-4 h-4" />
              </button>
            </div>

            <CashflowYearlyTable
              report={yearlyReport ?? null}
              selectedYear={selectedYear}
              onYearChange={setSelectedYear}
              isLoading={isLoadingYearly}
              communityName={communityName}
            />
          </div>
        </div>
      )}

      {/* Modal: Tambah/Edit Transaksi Manual */}
      <CashflowManualTransactionModal
        isOpen={isManualTxModalOpen}
        onClose={() => {
          setIsManualTxModalOpen(false);
          setTxToEdit(null);
        }}
        transactionToEdit={txToEdit}
        categories={categories}
        accounts={accountsData?.accounts ?? []}
        onSubmit={handleSaveManualTx}
        isLoading={createTxMutation.isPending || updateTxMutation.isPending}
      />

      {/* Modal: Tambah/Edit Kantong Kas */}
      <CashflowAddAccountModal
        isOpen={isAccountModalOpen}
        onClose={() => {
          setIsAccountModalOpen(false);
          setAccountToEdit(null);
        }}
        accountToEdit={accountToEdit}
        onSubmit={handleSaveAccount}
        onDelete={() => accountToEdit && handleDeleteAccount(accountToEdit)}
        isLoading={createAccountMutation.isPending || updateAccountMutation.isPending}
      />
    </div>
  );
};

export default CashflowManagementPage;
